// Sidebar navigation config — shared by AppLayout (sidebar), TabsContext and
// TabBar (tab titles). Kept out of AppLayout to avoid an import cycle.
import {
  LayoutDashboard,
  Users,
  Box,
  Grid3X3,
  Beaker,
  Calendar,
  CalendarDays,
  Flame,
  ClipboardCheck,
  Truck,
  BarChart3,
  Package,
  FileSpreadsheet,
  Database,
  Receipt,
  BookOpen,
  ListTodo,
  Search,
  NotebookPen,
  Wallet,
  DollarSign,
  Settings,
  Building2,
  ShoppingCart,
  ArrowDownLeft,
  ArrowUpRight,
  Download,
  Warehouse,
  Navigation,
  Paintbrush,
  ShieldCheck,
  Code,
  Smartphone,
  FileText,
  Activity,
  Sparkles,
  Brain,
  GitCompareArrows,
  Link2,
  Wrench,
  Star,
  MinusCircle,
  ClipboardList,
  Megaphone,
  Globe,
  Scale,
  Hash,
  Banknote,
  Zap,
  FlaskConical,
  PackageCheck,
  ListChecks,
} from "lucide-react";
import { REPORT_GROUPS, REPORTS_HOME_HREF } from "./reportsCatalog";

export const navigation = [
  // 1. Dashboard
  { name: "Dashboard", href: "/", icon: LayoutDashboard, permission: "view_dashboard" },
  // 2. Masters
  {
    name: "Masters",
    href: "/masters",
    icon: Database,
    children: [
      { name: "Customers", href: "/customers", icon: Users, permission: "view_customers", master: true },
      { name: "Castings", href: "/castings", icon: Box, permission: "view_castings", master: true },
      { name: "Patterns", href: "/patterns", icon: Grid3X3, permission: "view_patterns", master: true },
      { name: "Grades", href: "/grades", icon: Beaker, permission: "view_grades", master: true },
      { name: "Materials", href: "/materials", icon: Package, permission: "view_materials", master: true },
    ]
  },
  // 3. Marketing
  {
    name: "Marketing",
    href: "/marketing",
    icon: Megaphone,
    children: [
      { name: "Status", href: "/marketing/status", icon: BarChart3, permission: "view_marketing" },
      { name: "Companies", href: "/marketing", icon: Building2, permission: "view_marketing" },
      { name: "Leads", href: "/marketing/leads", icon: Star, permission: "view_marketing" },
      { name: "Customer Sales", href: "/marketing/customer-sales", icon: ShoppingCart, permission: "view_marketing" },
      { name: "Discover", href: "/marketing/discover", icon: Globe, permission: "add_marketing" },
    ]
  },
  // 4. Sales
  {
    name: "Sales",
    href: "/sales",
    icon: ClipboardCheck,
    children: [
      { name: "Enquiries", href: "/sales?tab=enquiries", icon: FileSpreadsheet, permission: "view_sales" },
      { name: "Quotations", href: "/sales?tab=quotations", icon: Receipt, permission: "view_sales" },
      { name: "Orders", href: "/sales?tab=orders", icon: Package, permission: "view_sales" },
      { name: "Schedules", href: "/sales?tab=schedules", icon: CalendarDays, permission: "view_sales" },
      { name: "Direct Orders", href: "/sales?tab=direct-orders", icon: Navigation, permission: "view_sales" },
      { name: "Sales Invoices", href: "/accounting/sales-invoices", icon: Receipt, permission: "view_sales_invoices" },
    ]
  },
  // 4. Purchase
  {
    name: "Purchase",
    href: "/purchase",
    icon: ShoppingCart,
    children: [
      { name: "Vendors", href: "/purchase/vendors", icon: Building2, permission: "view_vendors", master: true },
      { name: "Purchase Orders", href: "/purchase/orders", icon: FileSpreadsheet, permission: "view_purchase_orders" },
      { name: "Challans (GRN)", href: "/purchase/challans", icon: Package, permission: "view_purchase_challans" },
      { name: "Purchase Invoices", href: "/purchase/invoices", icon: Receipt, permission: "view_purchase_invoices" },
    ]
  },
  // 5. Sub Contract
  {
    name: "Sub Contract",
    href: "/sub-contract",
    icon: Wrench,
    permission: "view_machining",
    children: [
      { name: "Machining Vendors", href: "/sub-contract/machining-vendors", icon: Building2, permission: "view_machining", master: true },
      { name: "Machining Contracts", href: "/sub-contract/machining-contracts", icon: Wrench, permission: "view_machining" },
      { name: "Pattern Vendors", href: "/sub-contract/pattern-vendors", icon: Building2, permission: "view_machining", master: true },
      { name: "Pattern Contracts", href: "/sub-contract/pattern-contracts", icon: Paintbrush, permission: "view_machining" },
      { name: "Contract Invoices", href: "/sub-contract/contract-invoices", icon: Receipt, permission: "view_sales_invoices" },
    ],
  },
  // 6. Store
  {
    name: "Store",
    href: "/store",
    icon: Package,
    permission: "view_store",
    children: [
      { name: "Heat Transactions", href: "/store", icon: ArrowUpRight, permission: "view_store" },
      { name: "Material Request", href: "/store/requests", icon: ClipboardList, permission: "view_store" },
      { name: "Consumption", href: "/store/consumption", icon: MinusCircle, anyPermission: ["view_store", "view_store_consumption"] },
      { name: "History", href: "/store/history", icon: Activity, anyPermission: ["view_store", "view_store_history"] },
    ]
  },
  // 7. Production
  {
    name: "Production",
    href: "/monthly-planning",
    icon: Flame,
    children: [
      { name: "Pending Orders", href: "/sales/pending-orders", icon: ClipboardList, permission: "view_sales" },
      { name: "Monthly Plan", href: "/monthly-planning", icon: Calendar, permission: "view_monthly_plan" },
      { name: "Daily Plan", href: "/daily-planning", icon: CalendarDays, permission: "view_daily_plan" },
      { name: "Heat Entry", href: "/heats", icon: Flame, permission: "view_heats" },
      { name: "Quality", href: "/quality", icon: ClipboardCheck, permission: "view_quality" },
    ]
  },
  // 7b. ProSheet (was "Sub Connect"; routes still /sub-connect/*)
  {
    name: "ProSheet",
    href: "/sub-connect",
    icon: Link2,
    children: [
      { name: "Work Order Status", href: "/sub-connect/work-order-status", icon: ListChecks, permission: "view_work_order_status" },
      { name: "Fettling", href: "/fettling", icon: Scale, permission: "view_fettling" },
      { name: "Pattern Num", href: "/sub-connect/pattern-num", icon: Hash, permission: "view_pattern_num" },
      { name: "Ready Cheque", href: "/sub-connect/ready-cheques", icon: Banknote, permission: "view_ready_cheques" },
      { name: "MSEDCL Reading", href: "/sub-connect/msedcl-readings", icon: Zap, permission: "view_msedcl_readings" },
      { name: "Sand Testing Report", href: "/sub-connect/sand-testing", icon: FlaskConical, permission: "view_sand_testing_reports" },
      { name: "Closing Stock", href: "/sub-connect/closing-stock", icon: PackageCheck, permission: "view_closing_stock" },
    ]
  },
  // 8. Dispatch
  { name: "Dispatch", href: "/dispatch", icon: Truck, permission: "view_dispatch" },
  // 9. Stock
  {
    name: "Stock",
    href: "/stock",
    icon: Warehouse,
    permission: "view_stock",
    children: [
      { name: "Material Stock", href: "/stock?tab=materials", icon: Package, permission: "view_stock" },
      { name: "Casting Stock", href: "/stock?tab=castings", icon: Box, permission: "view_stock" },
      { name: "Pattern Stock", href: "/stock?tab=patterns", icon: Grid3X3, permission: "view_stock" },
    ]
  },
  // 10. Accounting (with TallyCO)
  {
    name: "Accounting",
    href: "/accounting",
    icon: DollarSign,
    children: [
      { name: "Day Book", href: "/accounting/day-book", icon: BookOpen, permission: "view_day_book" },
      { name: "Receivable", href: "/accounting/bills-receivable", icon: ArrowDownLeft, permission: "view_outstanding" },
      { name: "Bills Payable", href: "/accounting/bills-payable", icon: ArrowUpRight, permission: "view_outstanding" },
      { name: "Advances", href: "/accounting/advances", icon: Wallet, permission: "view_outstanding" },
      { name: "Connector", divider: true },
      { name: "TallyCO", href: "/tally-co", icon: Link2, permission: "view_chart_of_accounts" },
    ]
  },
  // 11. Reports
  {
    name: "Reports",
    href: "/reports",
    icon: BarChart3,
    anyPermission: ["view_report_master", "view_report_costing", "view_report_heats", "view_report_sales", "view_report_wip"],
    // Built from config/reportsCatalog.js — one divider per module group.
    children: [
      { name: "All Reports", href: REPORTS_HOME_HREF, anyPermission: ["view_report_master", "view_report_costing", "view_report_heats", "view_report_sales", "view_report_wip"] },
      ...REPORT_GROUPS.flatMap((g) => [
        { name: g.name, divider: true },
        ...g.reports.map((r) => ({ name: r.name, href: r.href, permission: r.permission })),
      ]),
    ]
  },
  // 12. System (with Status)
  {
    name: "System",
    href: "/system",
    icon: Settings,
    children: [
      { name: "Status", href: "/status", icon: Navigation, permission: "view_status" },
      { name: "Settings", href: "/settings", icon: Settings },
      { name: "Business Profile", href: "/system/business-profile", icon: Building2, permission: "view_business_profile" },
      { name: "Terms & Conditions", href: "/system/terms-conditions", icon: FileText, permission: "view_business_profile" },
      { name: "Users", href: "/system/users", icon: Users, permission: "view_users" },
      { name: "Dashboards", href: "/system/dashboards", icon: LayoutDashboard, permission: "manage_dashboards", adminOnly: true },
      { name: "Furnaces", href: "/system/furnaces", icon: Flame, permission: "view_furnaces" },
      { name: "Print Canvas", href: "/print-canvas", icon: Paintbrush, permission: "view_print_templates" },
      { name: "Import Data", href: "/import", icon: FileSpreadsheet, permission: "import_data" },
      { name: "Export Data", href: "/export", icon: Download, permission: "export_data" },
      { name: "Get Data", href: "/system/get-data", icon: FileSpreadsheet, permission: "export_data" },
      { name: "Notifications", href: "/system/notifications", icon: ClipboardCheck, permission: "send_notifications" },
      { name: "Approvals", href: "/system/approvals", icon: ShieldCheck, permission: "view_approvals" },
      { name: "Activity Log", href: "/system/activity", icon: Activity, permission: "view_activity_log" },
      { name: "User Activity", href: "/system/activity/users", icon: Users, permission: "view_activity_log" },
      { name: "Duplicates", href: "/system/duplicates", icon: GitCompareArrows, permission: "developer_access", adminOnly: true },
      { name: "Data", href: "/system/data", icon: Database, permission: "developer_access", adminOnly: true },
      { name: "Quick Access", href: "/system/quick-access", icon: Smartphone },
      { name: "AI Settings", href: "/system/ai-settings", icon: Sparkles, permission: "use_ai_assistant" },
      { name: "LLM Training", href: "/system/llm", icon: Brain, permission: "use_ai_assistant" },
      { name: "Developer", href: "/system/developer", icon: Code, permission: "developer_access", adminOnly: true },
      { name: "Developer Tools", href: "/system/developer-tools", icon: Wrench, permission: "developer_access", adminOnly: true },
      { name: "Maintenance", href: "/system/maintenance", icon: Wrench, permission: "developer_access", adminOnly: true },
    ]
  },
];

// Parents first so "/sales" resolves to "Sales", not its first child.
// Children without their own icon inherit the parent's (for tab icons).
const flatNav = navigation.flatMap((item) => [
  item,
  ...(item.children || []).filter((c) => !c.divider).map((c) => ({ ...c, icon: c.icon || item.icon })),
]);

// Nav entry best matching a tab/page href ("/sales?tab=orders" -> Orders).
const findNavForHref = (href) => {
  if (!href) return null;
  const exact = flatNav.find((n) => n.href === href);
  if (exact) return exact;
  const path = href.split("?")[0];
  const byPath = flatNav.find((n) => n.href.split("?")[0] === path);
  if (byPath) return byPath;
  // Detail pages: longest nav href that is a path prefix ("/purchase/vendors/3/ledger" -> "Vendors").
  // Compare PATH length, not full href — otherwise "/reports?tab=production-summary"
  // (long query) beat "/reports/grade-report" for "/reports/grade-report/detail".
  const pathLen = (n) => n.href.split("?")[0].length;
  return (
    flatNav
      .filter((n) => n.href !== "/" && path.startsWith(n.href.split("?")[0] + "/"))
      .sort((a, b) => pathLen(b) - pathLen(a))[0] || null
  );
};

export const getTitleForHref = (href) => {
  const match = findNavForHref(href);
  if (match) return match.name;
  const seg = (href || "").split("?")[0].split("/").filter(Boolean)[0];
  if (!seg) return "Dashboard";
  return seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
};

// Docs aren't in the sidebar nav, but they're tabbable — give their tabs the
// docs brand icon. The editor overrides the tab TITLE with the sheet name.
// The personal TODO list is the same shape: reached from the top-nav icon
// cluster, never the sidebar, but it still opens as a tab.
// Exce Reports is the same again — it's a Docs feature (opened per-doc via
// the "Exce Report" button on a doc's card), not its own Production module,
// so it has no sidebar entry either, just a tab icon.
export const getIconForHref = (href) => {
  const path = (href || "").split("?")[0];
  if (path === "/docs" || path.startsWith("/docs/")) return BookOpen;
  if (path === "/todos") return ListTodo;
  if (path === "/search") return Search;
  if (path === "/notes") return NotebookPen;
  if (path === "/exce-reports") return ClipboardCheck;
  return findNavForHref(href)?.icon || null;
};

// Docs-section tabs get a distinct accent color in the tab strip (green,
// vs. the orange used everywhere else) so it reads as a separate workspace.
export const isDocsHref = (href) => {
  const path = (href || "").split("?")[0];
  return path === "/docs" || path.startsWith("/docs/");
};
